/**
 * Turns events into "read this again", without reading it once per event.
 *
 * A move of a visit is heard two or three times (its status, the event of
 * its own, a line of the queue), and a busy hour brings many a second. Each
 * names what it makes stale; this waits a moment and asks for each thing
 * once. A longer wait for a costly answer makes it "at most once in that
 * long".
 */

/** How long a list waits for the rest of a burst. */
export const REFRESH_SOON_MS = 300;

export type Refresh = (queryKey: readonly unknown[], delayMs?: number) => void;

export class Refresher {
  readonly #run: (queryKey: readonly unknown[]) => void;
  readonly #pending = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(run: (queryKey: readonly unknown[]) => void) {
    this.#run = run;
  }

  /** Asks for everything under a key again, once, `delayMs` from the first call that asked. */
  schedule: Refresh = (queryKey, delayMs = REFRESH_SOON_MS) => {
    const id = JSON.stringify(queryKey);
    if (this.#pending.has(id)) return;
    this.#pending.set(
      id,
      setTimeout(() => {
        this.#pending.delete(id);
        this.#run(queryKey);
      }, delayMs),
    );
  };

  /** Forgets what waits: the stream is closed, or everything is being read anew anyway. */
  cancel(): void {
    for (const timer of this.#pending.values()) clearTimeout(timer);
    this.#pending.clear();
  }
}
